import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';

const ACCENT_CLASS = {
  red: 'text-[#dd1f26]',
  green: 'text-emerald-400',
};

// Motorsport broadcast status bar for /live.
// Rotates through varied broadcast items instead of repeating one string.
// Self-contained: subscribes to the live rig count so the ticker (rendered
// at the layout level) always shows the real number.
export default function LiveFeedTicker({ rigCount }) {
  const [internalCount, setInternalCount] = useState(rigCount ?? null);

  useEffect(() => {
    if (rigCount != null) return;
    let alive = true;
    const refresh = () =>
      base44.entities.RigLiveFeed.list('-sort_order', 50)
        .then((data) => { if (alive) setInternalCount((data || []).length); })
        .catch(() => {});
    refresh();
    const unsub = base44.entities.RigLiveFeed.subscribe(refresh);
    return () => { alive = false; unsub(); };
  }, [rigCount]);

  const count = rigCount ?? internalCount ?? 10;

  const items = [
    { liveDot: true, segs: [{ t: 'LIVE FEED' }] },
    { segs: [{ t: 'MOTION REALITY BROADCAST HUB' }] },
    { segs: [{ t: 'NORDSCHLEIFE', a: 'red' }, { t: ' LAP CHALLENGE ACTIVE' }] },
    { segs: [{ t: String(count), a: 'green' }, { t: ' RIGS MONITORED • ' }, { t: 'BROMBOROUGH UK', a: 'red' }] },
  ];

  // Repeat the item set enough times to fill the viewport for seamless scrolling
  const halfItems = Array.from({ length: 8 }).flatMap(() => items);

  const renderItem = (item, key, hidden = false) => (
    <span key={key} className="inline-flex items-center gap-2 px-4" aria-hidden={hidden || undefined}>
      {item.liveDot && (
        <span className="relative flex items-center justify-center">
          <span className="absolute w-2 h-2 rounded-full bg-emerald-400 opacity-75 animate-ping" />
          <span className="relative w-2 h-2 rounded-full bg-emerald-400" style={{ boxShadow: '0 0 6px 1px rgba(46,204,113,0.6)' }} />
        </span>
      )}
      <span className="font-mono font-bold text-[11px] tracking-[0.3em] leading-8 uppercase">
        {item.segs.map((seg, i) => (
          <span key={i} className={seg.a ? ACCENT_CLASS[seg.a] : 'text-white/70'}>{seg.t}</span>
        ))}
      </span>
      <span className="text-white/10 text-xs leading-8">·</span>
    </span>
  );

  return (
    <div className="fixed top-0 left-0 right-0 z-[55] h-8 overflow-hidden flex items-center carbon-bg bg-[#0a0a0c] border-b border-white/5">
      {/* Subtle green left-edge glow signalling live broadcast */}
      <div
        className="absolute left-0 top-0 bottom-0 w-24 pointer-events-none"
        style={{ background: 'linear-gradient(90deg, rgba(46,204,113,0.10) 0%, transparent 100%)' }}
      />
      {/* Rev-strip accent line along the bottom edge */}
      <div
        className="absolute bottom-0 left-0 right-0 h-[2px]"
        style={{
          background: 'linear-gradient(90deg, #2ecc71 0%, #2ecc71 27%, #f1c40f 27%, #f1c40f 54%, #e74c3c 54%, #e74c3c 78%, #9b59b6 78%, #9b59b6 100%)',
        }}
      />

      <div className="overflow-hidden whitespace-nowrap flex-1">
        <div className="inline-flex animate-marquee">
          {halfItems.map((item, i) => renderItem(item, `a-${i}`, i > 0))}
          {halfItems.map((item, i) => renderItem(item, `b-${i}`, true))}
        </div>
      </div>
    </div>
  );
}